import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
    LAYOUT_TYPE,
} from '../utils';

export const singleValueOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    align: {
        default: 'center',
        description: 'Specify how to align the center content.',
        pattern: getPattern(['left', 'center', 'right']),
        type: 'string',
    },
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color for the background. You may use a dataSource to apply the color. The default for enterprise light is "#ffffff". The default for enterprise dark is "#000000". The default for prisma dark is "#0b0c0e".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    majorColor: {
        default: '> themes.defaultFontColor',
        description:
            'Specify the color for the major value.  You may use a dataSource to apply the color. The hex value format should be “#FFFFFF”. The default for enterprise light is "#000000". The default for enterprise dark is "#ffffff". The default for prisma dark is "rgba(255, 255, 255, 0.98)".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    majorFontSize: {
        description:
            'Specify the font size (px) for the major value. By default the major value font size is calculated dynamically based on the available space.',
        type: 'number',
    },
    majorValue: {
        default: '> sparklineValues|lastPoint()',
        description:
            'The raw value to display and used to calculate trendValue. It is not displayed if majorValueDisplay is specified.',
        type: ['string', 'number'],
    },
    majorValueDisplay: {
        default: '> majorValue',
        description: 'The formatted version of majorValue displayed in the visualization.',
        type: ['string', 'number'],
    },
    majorValueField: {
        default: '> majorValue | getField()',
        description: 'The field name of major value.',
        type: 'string',
    },
    numberPrecision: {
        default: 0,
        maximum: 20,
        minimum: 0,
        description:
            'Specify the number of decimal places to display. For example, to display 3 decimal places, use a value of 3. Values can range from 0 to 20.',
        type: 'number',
    },
    shouldAbbreviateTrendValue: {
        default: false,
        description:
            'Specify whether to abbreviate the trend value to 2 decimal points. A magnitude unit will be displayed.',
        type: 'boolean',
    },
    shouldAbbreviateMajorValue: {
        default: false,
        description:
            'Specify whether to abbreviate the major value to 2 decimal points. A magnitude unit will be displayed.',
        type: 'boolean',
    },
    shouldSparklineAcceptNullData: {
        default: true,
        description:
            'Deprecated: Use sparklineNullValueDisplay instead. Specify whether to convert null and non-numeric values in the sparkline to 0.',
        type: 'boolean',
    },
    sparklineNullValueDisplay: {
        default: 'gaps',
        description:
            'Specify the handling of null values for sparkline. "gaps" shows breaks in the line (default), "zero" treats null as 0, "connect" connects points across nulls.',
        pattern: getPattern(['gaps', 'zero', 'connect']),
        type: 'string',
    },
    shouldUseThousandSeparators: {
        default: true,
        description: 'Specify whether numeric values use commas as thousandths separators.',
        type: 'boolean',
    },
    showSparklineAreaGraph: {
        default: false,
        description: 'Specify whether to show the sparkline as an area graph rather than a line.',
        type: 'boolean',
    },
    showSparklineTooltip: {
        default: false,
        description: 'Show a tooltip to display values on the sparkline.',
        type: 'boolean',
    },
    splitByLayout: {
        default: 'off',
        description:
            'Specify the layout method by which to display the visualization, which splits the data into individual visualizations based on a certain category.',
        type: 'string',
        pattern: getPattern([LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis]),
        enum: [LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis],
    },
    sparklineAreaColor: {
        default: '> sparklineStrokeColor',
        description:
            'Specify the color for the sparkline area fill. You may use a dataSource to apply the color. The hex value format should be “#FFFFFF”. The graph area will have an opacity of 20% and will blend with the background color.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    sparklineDisplay: {
        default: 'below',
        description: 'Specify how to display a sparkline.',
        pattern: getPattern(['before', 'after', 'below', 'off']),
        type: 'string',
    },
    sparklineHighlightDots: {
        default: 0,
        description:
            'Specify the number of markers, or dots, to display at the top of a sparkline area graph.',
        type: 'number',
    },
    sparklineHighlightSegments: {
        default: 0,
        description: 'Specify the number of segments to be highlighted at the top of a sparkline area graph.',
        type: 'number',
    },
    sparklineStrokeColor: {
        default: '> themes.defaultSparklineStrokeColor',
        description:
            'Specify the color for the sparkline stroke. You may use a dataSource to apply the color. The hex value format should be “#FFFFFF”. The default for enterprise light is "#000000". The default for enterprise dark is "#ffffff". The default for prisma dark is "rgba(255, 255, 255, 0.98)".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    sparklineValues: {
        default: '> primary|seriesByPrioritizedTypes("number", "string", "time")',
        description:
            'List of numerical values to display on a sparkline. If the values are string type, the sparkline will not be shown.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    trendColor: {
        default: '> themes.defaultFontColor',
        description:
            'Specify the color for the trend value. You may use a dataSource to apply the color. The hex value format should be “#FFFFFF”. The default for enterprise light is "#000000". The default for enterprise dark is "#ffffff". The default for prisma dark is "rgba(255, 255, 255, 0.98)".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    trendDisplay: {
        default: 'absolute',
        description: 'Specify how to display the trend value.',
        pattern: getPattern(['percent', 'absolute', 'off']),
        type: 'string',
    },
    trendFontSize: {
        description:
            'Specify the font size (px) for the trend value. By default the trend value font size is calculated dynamically based on the available space.',
        type: 'number',
    },
    trendValue: {
        default: '> sparklineValues|delta(-2)',
        description: 'The trend value to display in the visualization.',
        type: 'number',
    },
    trellisBackgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color used for the trellis container background by using a hexadecimal code. For example, #0000FF.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    trellisColumns: {
        description:
            'Specify the number of visualizations to display in a given row of the trellis container. The remaining visualizations will wrap accordingly. If nothing is specified, it will be auto-set based on the trellisMinColumnWidth. The minimum value is 1.',
        type: 'number',
        minimum: 1,
    },
    trellisMinColumnWidth: {
        default: 100,
        minimum: 1,
        description:
            'Specify the minimum width, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisPageCount: {
        default: 20,
        minimum: 1,
        description:
            'Specify the maximum number of visualizations to display in a single page in the trellis container. The remaining visualizations will paginate accordingly. The minimum value is 1.',
        type: 'number',
    },
    trellisRowHeight: {
        default: 70,
        minimum: 1,
        description:
            'Specify the height, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisSortBy: {
        default: 'result',
        description:
            'Specify the sort method for the individual visualizations in the trellis container. Use result to sort by search result order, name to sort alphabetically by visualization label, value to sort by the major value, or trend to sort by the trend value.',
        type: 'string',
        pattern: getPattern(['result', 'name', 'value', 'trend']),
        enum: ['result', 'name', 'value', 'trend'],
    },
    trellisSortOrder: {
        default: 'ascending',
        description:
            'Specify the sort order for the individual visualizations in the trellis container. This setting applies to the sort method specified in trellisSortBy.',
        type: 'string',
        pattern: getPattern(['ascending', 'descending']),
        enum: ['ascending', 'descending'],
    },
    trellisSplitBy: {
        description:
            'Specify the field name of the column with categories used, or “aggregations”, to split the data into individual visualizations for trellis display, if applicable. If a SPL `timechart` command is used, this may default to the SPL `by` clause field. If a SPL `chart` command is used, this may default to the first string field.',
        type: 'string',
    },
    underLabel: {
        description: 'Specify the text that appears below the major value.',
        type: 'string',
    },
    underLabelColor: {
        default: '> majorColor',
        description:
            'Specify the color for the under label text. You may use a dataSource to apply the color. The hex value format should be “#FFFFFF”. By default it follows the major value color.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    underLabelFontSize: {
        default: 12,
        description: 'Specify the font size (px) for the under label text.',
        type: 'number',
    },
    unit: {
        description: 'Specify text to show next to the major value.',
        type: 'string',
    },
    unitPosition: {
        default: 'after',
        description: 'Specify whether the unit text should appear before or after the major value.',
        pattern: getPattern(['before', 'after']),
        type: 'string',
    },
});
